import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe as GlobeIcon } from 'lucide-react';
import { ChromeIcon } from '../common/BrandIcons';
import { useSSPWallet } from '@/hooks/useSSPWallet';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setSession, setAuthStep, setError, reset } from '@/store/authSlice';
import { authApi } from '@/api/auth';
import { friendlyError } from '@/lib/errorMessage';
import type { AuthStep } from '@/types/auth';

interface StepConfig {
  title: string;
  description: string;
}

export function ConnectWallet() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { authStep, error: authError } = useAppSelector((state) => state.auth);
  const { isInstalled, isLoading, error: walletError, wkSign, clearError } = useSSPWallet();

  const stepConfig: Record<AuthStep, StepConfig> = {
    idle: {
      title: t('login.wallet.idleTitle'),
      description: t('login.wallet.idleDesc'),
    },
    signing: {
      title: t('login.wallet.signingTitle'),
      description: t('login.wallet.signingDesc'),
    },
    verifying: {
      title: t('login.wallet.verifyingTitle'),
      description: t('login.wallet.verifyingDesc'),
    },
    authenticated: {
      title: t('login.wallet.authenticatedTitle'),
      description: t('login.wallet.authenticatedDesc'),
    },
    error: {
      title: t('login.wallet.errorTitle'),
      description: t('login.wallet.errorDesc'),
    },
  };

  const error = walletError ?? authError;

  const handleSignIn = useCallback(async () => {
    clearError();
    dispatch(setAuthStep('signing'));

    const signResult = await wkSign('Sign in to SSP Enterprise');

    if (!signResult) {
      dispatch(setAuthStep('error'));
      return;
    }

    dispatch(setAuthStep('verifying'));

    try {
      if (!signResult.keySignature || !signResult.keyPubKey) {
        dispatch(setError(t('login.wallet.missingKeySignature')));
        return;
      }

      const response = await authApi.loginWithWK({
        message: signResult.message,
        walletSignature: signResult.walletSignature,
        walletPubKey: signResult.walletPubKey,
        keySignature: signResult.keySignature,
        keyPubKey: signResult.keyPubKey,
        witnessScript: signResult.witnessScript,
        wkIdentity: signResult.wkIdentity,
        // EL-2: proves this tab requested the challenge (memory only).
        ...(signResult.challengeToken ? { challengeToken: signResult.challengeToken } : {}),
      });

      dispatch(
        setSession({
          sessionToken: response.sessionToken,
          user: response.user,
          expiresAt: response.expiresAt,
          loginMethod: 'wk',
        })
      );
    } catch (err) {
      dispatch(setError(friendlyError(err, t, 'login.wallet.verifyFailed')));
    }
  }, [wkSign, clearError, dispatch, t]);

  const handleCancel = useCallback(() => {
    clearError();
    dispatch(reset());
  }, [clearError, dispatch]);

  const currentStep = stepConfig[authStep];
  const stepIndex = authStep === 'idle' ? 0 : authStep === 'signing' ? 1 : 2;

  // SSP Wallet not installed
  if (!isInstalled) {
    return (
      <>
        <h2 className="login-card-title">{t('login.wallet.getWallet')}</h2>
        <p className="login-card-description">{t('login.wallet.getWalletDesc')}</p>
        <div className="install-section">
          <a
            href="https://chromewebstore.google.com/detail/ssp-wallet/mgfbabcnedcejkfibpafadgkhmkifhbd"
            target="_blank"
            rel="noopener noreferrer"
            className="install-btn"
          >
            <ChromeIcon className="install-btn-icon" />
            {t('login.wallet.installChrome')}
          </a>
          <a
            href="https://addons.mozilla.org/en-US/firefox/addon/ssp-wallet"
            target="_blank"
            rel="noopener noreferrer"
            className="install-btn"
          >
            <GlobeIcon className="install-btn-icon" />
            {t('login.wallet.installFirefox')}
          </a>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Steps indicator */}
      <div className="steps-indicator">
        <div
          className={`step-dot ${stepIndex === 0 ? 'active' : stepIndex > 0 ? 'completed' : ''}`}
        />
        <div
          className={`step-dot ${stepIndex === 1 ? 'active' : stepIndex > 1 ? 'completed' : ''}`}
        />
        <div className={`step-dot ${stepIndex === 2 ? 'active' : ''}`} />
      </div>

      <div className="status-container">
        <h2 className="status-title">{currentStep.title}</h2>
        <p className="status-description">{currentStep.description}</p>

        {error && <div className="error-message">{error}</div>}

        {(isLoading || authStep === 'signing' || authStep === 'verifying') && (
          <div className="spinner" />
        )}

        {authStep === 'idle' && (
          <button className="btn-primary" onClick={handleSignIn} disabled={isLoading}>
            {t('login.wallet.signIn')}
          </button>
        )}

        {authStep === 'error' && (
          <>
            <button className="btn-primary" onClick={handleSignIn}>
              {t('login.wallet.tryAgain')}
            </button>
            <button className="btn-secondary" onClick={handleCancel} style={{ marginTop: 12 }}>
              {t('login.wallet.cancel')}
            </button>
          </>
        )}

        {authStep === 'signing' && (
          <button className="btn-secondary" onClick={handleCancel}>
            {t('login.wallet.cancel')}
          </button>
        )}
      </div>
    </>
  );
}

export default ConnectWallet;
